import * as React from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { KlaraDocument, KlaraEvaluation } from '../../../model/klara';
import { useAppTranslation } from '../../../services/i18n';

type Props = {
  evaluation: KlaraEvaluation | null | undefined;
  protocol?: KlaraDocument;
  protocolUrl?: string;
};

/** Verdicts where the sources are weak enough that the user should be warned. */
const WEAK_VERDICTS = ['revise', 'missing', 'only_video'];

export function isWeak(evaluation: KlaraEvaluation | null | undefined) {
  return WEAK_VERDICTS.includes(evaluation?.verdict ?? '');
}

/**
 * Scores from the newest evaluation_completed callback.
 *
 * Only accuracy, coverage and quality are shown — `elearning` is deliberately
 * omitted, per the partner's own UI mapping. The numbers describe the version
 * sent in message 2 and are not re-labelled after local edits: once the score
 * gates nothing, its staleness is cosmetic.
 */
export function EvaluationBand({ evaluation, protocol, protocolUrl }: Props) {
  const t = useAppTranslation();
  if (!evaluation) return null;

  const scores = evaluation.component_scores ?? {};

  return (
    <>
      <Box
        sx={{
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          color: 'var(--color-white)',
          borderRadius: '12px',
          padding: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '32px',
          marginBottom: '16px',
        }}
      >
        <ScoreBadge score={evaluation.theme_score} weak={isWeak(evaluation)} />
        <Typography sx={{ flexGrow: 1, fontSize: '18px', fontWeight: 700 }}>
          {t('klara.evaluation.title')}
        </Typography>

        <Score label={t('klara.evaluation.accuracy')} value={scores.accuracy} />
        <Score label={t('klara.evaluation.coverage')} value={scores.coverage} />
        <Score label={t('klara.evaluation.quality')} value={scores.quality} />

        {protocol?.available && protocolUrl && (
          <Button
            variant="contained"
            startIcon={<DescriptionOutlinedIcon />}
            href={protocolUrl}
            // A white pill on the dark band, as drawn — MUI's `inherit` gives
            // it a translucent fill that reads as disabled against this ground.
            sx={{
              flexShrink: 0,
              height: '40px',
              borderRadius: '8px',
              padding: '0 16px',
              whiteSpace: 'nowrap',
              backgroundColor: 'var(--color-white)',
              color: 'var(--color-black)',
              '&:hover': { backgroundColor: 'var(--color-white)' },
            }}
          >
            {t('klara.evaluation.downloadProtocol')}
          </Button>
        )}
      </Box>

      {isWeak(evaluation) && (
        <Alert severity="warning" sx={{ marginBottom: '16px' }}>
          {t(`klara.evaluation.verdict.${evaluation.verdict}`)}
        </Alert>
      )}

      <ConsistencyWarnings evaluation={evaluation} />
    </>
  );
}

/**
 * The overall score as the ringed disc the design draws.
 *
 * The design gives two samples and no rule: amber at 72 % on the source
 * evaluation, green at 92 % on the result. Rather than invent a numeric
 * threshold, the ring follows the partner's own verdict — the same signal that
 * already decides whether the generate button stops to ask. A number we cannot
 * colour is left grey rather than guessed at.
 */
export function ScoreBadge({
  score,
  weak,
}: {
  score?: number | null;
  weak: boolean;
}) {
  const known = score !== undefined && score !== null;
  const color = !known
    ? 'var(--color-white50)'
    : weak
    ? 'var(--color-warning)'
    : 'var(--color-primary)';

  return (
    <Box
      sx={{
        flexShrink: 0,
        width: '52px',
        height: '52px',
        borderRadius: '50%',
        border: `3px solid ${color}`,
        backgroundColor: 'var(--color-black-text)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color,
        fontSize: '16px',
        fontWeight: 700,
      }}
    >
      {known ? `${Math.round(score)}%` : '—'}
    </Box>
  );
}

function Score({ label, value }: { label: string; value?: number }) {
  if (value === undefined || value === null) return null;

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography
        sx={{
          display: 'block',
          fontSize: '10.5px',
          fontWeight: 700,
          letterSpacing: '0.4px',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: '15px', fontWeight: 700 }}>
        {formatScore(value)}
      </Typography>
    </Box>
  );
}

/**
 * Only `severity: critical` conflicts, which the partner defines as safety,
 * health or legal obligations — contradictions between the customer's own
 * documents, quoted from both. Generation cannot fix these, so staying silent
 * would ship a compliance course with a contradiction we detected.
 */
function ConsistencyWarnings({ evaluation }: { evaluation: KlaraEvaluation }) {
  const t = useAppTranslation();
  const conflicts = (evaluation.consistency?.conflicts ?? []).filter(
    (conflict) => conflict.severity === 'critical'
  );
  if (conflicts.length === 0) return null;

  return (
    <Alert severity="error" sx={{ marginBottom: '16px' }}>
      <Typography sx={{ fontWeight: 600 }}>
        {t('klara.evaluation.conflictsTitle', { count: conflicts.length })}
      </Typography>
      {conflicts.map((conflict, index) => (
        <Box key={index} sx={{ marginTop: '8px' }}>
          <Typography sx={{ fontWeight: 600 }}>{conflict.topic}</Typography>
          <Typography variant="body2">
            {conflict.a?.filename}: “{conflict.a?.claim}”
          </Typography>
          <Typography variant="body2">
            {conflict.b?.filename}: “{conflict.b?.claim}”
          </Typography>
          {conflict.note && (
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              {conflict.note}
            </Typography>
          )}
        </Box>
      ))}
    </Alert>
  );
}

function formatScore(value?: number | null) {
  if (value === undefined || value === null) return '—';
  return `${Math.round(value)} %`;
}
